import "ag-charts-enterprise";
import { AgChartInstance, AgChartOptions } from "ag-charts-enterprise";
import { AgCharts } from "ag-charts-react";
import { useTheme } from "next-themes";
import { SetStateAction, useEffect, useMemo, useState } from "react";
import MapSummaryBubbles from "./MapSummaryBubbles";
// import { data } from "./data";
import {
  returnFormattedDate,
  returnSizeFormatted,
} from "@/lib/globalFormatterHelper";

import { IBaseAnalyticsCombined } from "@/store/analyticsDataStore";
import { useRef } from "react";
import { filterByInstrumentTypeAll } from "../analyticsHelperFunctions";
import {
  filterByCountryName,
  filterByDates,
  listOfCountriesInArray,
} from "./ClientSummaryReportHelpers";
import { IClientSummaryFilters } from "./IndexClientSummary";
import { topology } from "./topology";

function filterCountryData(
  data: IBaseAnalyticsCombined[],
  dataFilters: IClientSummaryFilters,
  countriesToTrack: string[],
) {
  const countryFiltered = filterByCountryName(data, dataFilters);
  const instrumentFilteredData = filterByInstrumentTypeAll(
    countryFiltered,
    dataFilters,
  );
  const dateFilteredData = filterByDates(instrumentFilteredData, dataFilters);

  const totals = dateFilteredData.reduce(
    (acc, item) => {
      if (countriesToTrack.includes(item.issueCountry)) {
        acc[item.issueCountry] =
          (acc[item.issueCountry] || 0) + Number(item.DV01);
      }
      return acc;
    },
    {} as Record<string, number>,
  );

  const finalArr = countriesToTrack.map((country) => ({
    name: country,
    value: totals[country] || 0,
  }));

  const numberOfObjects = finalArr.reduce((acc: number, obj: any) => {
    if (obj.value > 0) {
      acc++;
    }
    return acc;
  }, 0);

  if (numberOfObjects == 0) {
    return [];
  }
  return finalArr;
}

const MapChart = ({
  data,
  dataFilters,
  setDataFilters,
}: {
  data: IBaseAnalyticsCombined[];
  dataFilters: IClientSummaryFilters;
  setDataFilters: React.Dispatch<SetStateAction<IClientSummaryFilters>>;
}) => {
  // Handle light and dark mode:
  const { theme } = useTheme();
  const chartRef = useRef<AgChartInstance | null>(null);

  const [countriesToTrack, setCountriesToTrack] = useState(
    listOfCountriesInArray(data),
  );

  useEffect(() => {
    setCountriesToTrack(listOfCountriesInArray(data));
  }, [data]);

  const [chartData, setChartData] = useState(
    filterCountryData(data, dataFilters, countriesToTrack),
  );

  useEffect(() => {
    setChartData(filterCountryData(data, dataFilters, countriesToTrack));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dataFilters]);
  function handleCountryClick(event: any) {
    if (
      (dataFilters && dataFilters.countryActive === "") ||
      (dataFilters && dataFilters.countryActive != event.datum.name)
    ) {
      setDataFilters({ ...dataFilters, countryActive: event.datum.name });
    } else if (dataFilters && dataFilters.countryActive == event.datum.name) {
      handleBackgroundClick();
    }
  }

  function handleBackgroundClick() {
    if (dataFilters) {
      setDataFilters({ ...dataFilters, countryActive: "" });
    }
  }

  const options = useMemo<AgChartOptions>(() => {
    return {
      theme: theme == "dark" ? "ag-default-dark" : "ag-default",
      background: {
        visible: false,
      },
      data: chartData,
      topology,
      series: [
        {
          type: "map-shape-background",
        },
        {
          type: "map-shape",
          title: "Client Summary",
          colorKey: "value",
          idKey: "name",
          colorRange:
            theme == "dark" ? ["#fcd7b6", "#FC6A08"] : ["#fcd7b6", "#FC6A08"],
          listeners: {
            nodeClick: (event) => handleCountryClick(event),
          },
          // labelKey: "CurrencyLeg1",
          // colorKey: "value",
          // colorName: "Number",
          tooltip: {
            renderer: ({ datum }) => {
              // Customize tooltip content based on datum
              return `
            <div style="padding: 8px; font-family: sans-serif; font-size: 12px;">
               <div style="font-weight: bold; padding-bottom: 4px; font-size: 1rem">${datum.name}:</div>
              <div style="display: flex; gap: 24px;">
              <div style="display: flex; flex-direction: column; gap: 4px;">
              <div style="display: flex; gap: 2px;" ><div><strong>Total Activity: </strong></div> <div> $${datum.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} DV01</div></div>
              </div>
            </div>
            `;
            },
          },
        },
      ],
      listeners: {
        click: (event: any) => {
          // If datum is undefined, click was not on a node
          if (!event.datum) {
            handleBackgroundClick();
          }
        },
      },
      gradientLegend: {
        enabled: true,
        position: "top",
        gradient: {
          thickness: 10, // thickness (bar height)
          preferredLength: 800, // desired horizontal length
        },
        scale: {
          label: {
            fontSize: 8,
            formatter: ({ value }) => `${returnSizeFormatted(value)}`,
          },
        },
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFilters, theme, chartData]);

  return (
    <div
      className={`${theme == "dark" ? "agChartCubeBorderDark" : "agChartCubeBorder"} ${dataFilters.countryActive ? "!border-blue-100 dark:!border-slate-600" : ""} border-thin flex h-full w-full`}
    >
      <div className="relative flex h-full w-full overflow-hidden">
        <div
          id="countryFilterNameContainer"
          className="absolute bottom-44 left-2 z-20"
        >
          {(dataFilters.countryActive || dataFilters.activeDay) && (
            <div className="relative flex flex-col pl-2 text-xs text-gray-500 dark:text-gray-500">
              <div className="relative flex pb-1 pl-2 text-xs text-gray-500 dark:text-gray-500">
                Filtering:
              </div>

              {dataFilters.activeDay && (
                <div className="relative flex pl-4 text-xs text-gray-500 dark:text-gray-500">
                  Day: {returnFormattedDate(dataFilters.activeDay)}
                </div>
              )}
              {dataFilters.countryActive && (
                <div className="relative flex pl-4 text-xs text-gray-500 dark:text-gray-500">
                  Country: {dataFilters.countryActive}
                </div>
              )}
            </div>
          )}
        </div>

        <AgCharts
          ref={chartRef}
          className={`m-0 flex h-full w-full p-0`}
          options={options}
        />
        <MapSummaryBubbles data={data} dataFilters={dataFilters} />
      </div>
    </div>
  );
};

export default MapChart;
